import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2, ArrowLeft, Info } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api, useAuth } from "@/lib/auth-context";
import { usePricing } from "@/lib/use-pricing";

export default function BillingPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { plans, meta, loading: plansLoading, error: plansError } = usePricing();
  const [loading, setLoading] = useState(null);
  const [mockMode, setMockMode] = useState(false);
  const [cycle, setCycle] = useState("monthly"); // monthly | annual

  useEffect(() => {
    api.get("/billing/status").then(({ data }) => setMockMode(!!data.mock_mode)).catch(() => {});
  }, []);

  const handleSubscribe = async (planKey) => {
    setLoading(planKey);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan: planKey,
        billing_cycle: cycle,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed. Try again.");
    } finally {
      setLoading(null);
    }
  };

  const currentPrice = (p) =>
    cycle === "annual" ? p.annual_monthly_equivalent_usd : p.monthly_amount_usd;
  const priceSuffix = cycle === "annual" ? "/mo, billed annually" : "/month";

  return (
    <div data-testid="billing-page" className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-700 hover:text-[#0A192F]">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back</span>
          </Link>
          <p className="font-heading text-sm font-semibold text-[#0A192F]">Choose a plan</p>
          <button
            data-testid="billing-logout-btn"
            onClick={() => {
              logout();
              navigate("/");
            }}
            className="text-sm text-slate-600 hover:text-[#0A192F]"
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-3">
            Welcome, {user?.name}
          </p>
          <h1 className="font-heading text-4xl lg:text-5xl font-bold text-[#0A192F]">
            Subscribe to unlock matched cases
          </h1>
          <p className="mt-4 text-slate-600">
            Your account is approved. Pick a plan to access attorney-ready cases in your
            jurisdiction and practice areas.
          </p>

          {/* Monthly / Annual toggle */}
          <div
            data-testid="billing-cycle-toggle"
            className="mt-8 inline-flex items-center rounded-full border border-slate-200 bg-white p-1 shadow-sm"
          >
            <button
              data-testid="billing-cycle-monthly"
              onClick={() => setCycle("monthly")}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition ${
                cycle === "monthly"
                  ? "bg-[#0A192F] text-white"
                  : "text-slate-600 hover:text-[#0A192F]"
              }`}
            >
              Monthly
            </button>
            <button
              data-testid="billing-cycle-annual"
              onClick={() => setCycle("annual")}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition flex items-center gap-2 ${
                cycle === "annual"
                  ? "bg-[#0A192F] text-white"
                  : "text-slate-600 hover:text-[#0A192F]"
              }`}
            >
              Annual
              <span className="text-[10px] uppercase tracking-wider bg-[#C5A059] text-[#0A192F] px-1.5 py-0.5 rounded-full">
                Save ~17%
              </span>
            </button>
          </div>

          {mockMode && (
            <div
              data-testid="mock-mode-banner"
              className="mt-6 mx-auto max-w-xl bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 text-left"
            >
              <Info className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900 leading-relaxed">
                <strong>Test mode (mock billing):</strong> Stripe keys are not configured yet.
                Clicking subscribe will simulate a successful payment so you can preview the
                dashboard. Set
                <code className="mx-1 px-1.5 py-0.5 bg-amber-100 rounded">STRIPE_API_KEY</code>
                in production to enable real Stripe Checkout.
              </div>
            </div>
          )}
        </div>

        {plansLoading ? (
          <div className="mt-16 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : plansError ? (
          <p className="mt-16 text-center text-sm text-rose-600">{plansError}</p>
        ) : (
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {plans.map((p) => (
              <div
                key={p.plan_key}
                data-testid={`billing-plan-${p.plan_key}`}
                className={`relative p-8 rounded-2xl border bg-white ${
                  p.highlight
                    ? "border-[#C5A059] shadow-xl lg:-translate-y-3"
                    : "border-slate-200 shadow-sm"
                }`}
              >
                {p.highlight && (
                  <span className="absolute top-4 right-4 text-xs tracking-[0.18em] uppercase font-semibold text-[#0A192F] bg-[#C5A059] px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
                <p className="font-heading text-xl font-semibold text-[#0A192F]">{p.name}</p>
                <p className="mt-1 text-sm text-slate-500">{p.tagline}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span
                    data-testid={`billing-plan-${p.plan_key}-price`}
                    className="font-heading text-5xl font-bold text-[#0A192F]"
                  >
                    ${Math.round(currentPrice(p))}
                  </span>
                  <span className="text-slate-500">{priceSuffix}</span>
                </div>
                {cycle === "annual" && (
                  <p className="mt-1 text-xs text-slate-500">
                    ${p.annual_amount_usd.toLocaleString()} charged once per year
                  </p>
                )}
                <ul className="mt-8 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-[#C5A059] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`subscribe-${p.plan_key}-btn`}
                  onClick={() => handleSubscribe(p.plan_key)}
                  disabled={loading === p.plan_key}
                  className={`mt-8 w-full rounded-lg ${
                    p.highlight
                      ? "bg-[#0A192F] hover:bg-[#112A46] text-white"
                      : "bg-white border border-slate-300 text-[#0A192F] hover:bg-slate-50"
                  }`}
                >
                  {loading === p.plan_key ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : cycle === "annual" ? (
                    `Subscribe — $${p.annual_amount_usd.toLocaleString()}/yr`
                  ) : (
                    `Subscribe — $${p.monthly_amount_usd}/mo`
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}

        <p className="mt-10 text-center text-xs text-slate-500">
          Payments processed securely by Stripe. Cancel anytime from your billing portal.
          {meta.trial_days ? ` Includes a ${meta.trial_days}-day free Firm Pro trial.` : ""}
        </p>
      </div>
    </div>
  );
}
